import { UserInfo } from "pages/auth/types"

/**
 * Check if permissions array is valid and handle empty array case
 * @param userInfo - User information from Redux state
 * @returns true if permissions array is empty (allow all), false if invalid, undefined if valid with data
 */
const validatePermissions = (userInfo: UserInfo): boolean | undefined => {
  if (!userInfo?.permissions || !Array.isArray(userInfo.permissions)) {
    return false
  }

  // If permissions array is empty, allow all permissions
  if (userInfo.permissions.length === 0) {
    return true
  }

  return undefined // Valid array with data
}

/**
 * Check if user has a specific permission
 * @param userInfo - User information from Redux state
 * @param permissionKey - Permission key to check
 * @returns true if user has permission, false otherwise
 */
export const hasPermission = (
  userInfo: UserInfo,
  permissionKey: string
): boolean => {
  const validation = validatePermissions(userInfo)

  if (validation === false) return false
  if (validation === true) return true

  return userInfo.permissions.includes(permissionKey)
}

/**
 * Check if user has any of the specified permissions
 * @param userInfo - User information from Redux state
 * @param permissionKeys - Array of permission keys to check
 * @returns true if user has at least one permission, false otherwise
 */
export const hasAnyPermission = (
  userInfo: UserInfo,
  permissionKeys: string[]
): boolean => {
  const validation = validatePermissions(userInfo)

  if (validation === false) return false
  if (validation === true) return true

  return permissionKeys.some((key) => userInfo.permissions.includes(key))
}

/**
 * Check if user has all of the specified permissions
 * @param userInfo - User information from Redux state
 * @param permissionKeys - Array of permission keys to check
 * @returns true if user has all permissions, false if missing any permission
 */
export const hasAllPermissions = (
  userInfo: UserInfo,
  permissionKeys: string[]
): boolean => {
  const validation = validatePermissions(userInfo)

  if (validation === false) return false
  if (validation === true) return true

  return permissionKeys.every((key) => userInfo.permissions.includes(key))
}

/**
 * Get all user permissions
 * @param userInfo - User information from Redux state
 * @returns Array of permissions or empty array if none
 */
export const getUserPermissions = (userInfo: UserInfo): string[] => {
  if (!userInfo?.permissions || !Array.isArray(userInfo.permissions)) {
    return []
  }

  return [...userInfo.permissions]
}
